// Copyright (c) StrangeBee 2024

import RouteBuilder from '@Services/RouteBuilder';
import {
    AlertController,
    AlertInput,
    CustomFieldParams,
    AlertOutput,
    CaseCustomField,
    CaseOutput,
    UserOutput,
    AttachmentAlertType,
} from 'thehive-sdk';

import { convertCustomFields } from '@Helpers/customField';
import { queryClient } from '@Client';

export interface AlertEventList {
    onEdit: (alertData: Partial<AlertInput>, alertId: string, forceToStart?: boolean) => Promise<void | AlertOutput>;
    onBulkEdit: (
        caseData: Partial<AlertInput>,
        caseIds: string[],
        forceToStart?: boolean,
    ) => Promise<void | AlertInput>;
    onFollow: (alertId: string) => void;
    onImport: (alertId: string, caseId: string) => Promise<CaseOutput | void>;
    onMultipleFollow: (alertIds: string[]) => Promise<void>;
    onMultipleUnfollow: (alertIds: string[]) => Promise<void>;
    onUnfollow: (alertId: string) => void;
    onUnlinkFromCase: (alertId: string, caseId: string) => void;
    onDelete: (alertId: string) => void;
    onBulkDelete: (alertIds: string[]) => Promise<void>;
    onMergeSeveralIntoCase: (alertIds: string[], caseId: string) => Promise<void | CaseOutput>;
    customField: { onSet: (caseId: string, customFieldData: CustomFieldParams) => Promise<AlertOutput> };
    attachment: {
        onAdd: (
            alertId: string,
            attachments: FormData,
            refetch?: boolean,
        ) => Promise<{ attachments: AttachmentAlertType[] }>;
        onDelete: (alertId: string, attachmentId: string) => Promise<void>;
        onDownload: (alertId: string, attachmentId: string) => Promise<Blob>;
    };
}

interface AlertEventListParams {
    locale: (label: string) => string;
    controller: AlertController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
    history: any;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
    history,
}: AlertEventListParams): AlertEventList => ({
    onEdit: (alertData, alertId, forceToStart = false) => {
        // check if it's only an assign edit
        const propertiesUpdated = Object.keys(alertData);
        const isOnlyBulkAssign = propertiesUpdated.length === 1 && propertiesUpdated.includes('assignee');

        // if it is, no need to update status and assignee with currentUser
        let dataToEdit = [];
        if (isOnlyBulkAssign) {
            dataToEdit = Object.entries(alertData);
        } else {
            const currentUser = queryClient.getQueryData<UserOutput>(['currentUser']);
            const opts =
                forceToStart && !alertData.status ? { status: 'InProgress', assignee: currentUser?.login } : {};
            dataToEdit = Object.entries({ ...alertData, ...opts });
        }

        return controller
            .edit(
                dataToEdit.reduce(
                    (acc, [key, value]) =>
                        key === 'customFields'
                            ? { ...acc, customFields: convertCustomFields(value as CaseCustomField[]) }
                            : { ...acc, [key]: value },
                    {},
                ),
                alertId,
            )
            .then(() => onSuccess(false, locale('notification.alert.edit')))
            .catch(onError);
    },
    onBulkEdit: (
        alertData: Partial<AlertInput>,
        alertIds: string[],
        forceToStart = false,
    ): Promise<void | AlertInput> => {
        // check if it's only an assign bulk edit
        const propertiesUpdated = Object.keys(alertData);
        const isOnlyBulkAssign =
            propertiesUpdated.length === 3 &&
            propertiesUpdated.includes('assignee') &&
            propertiesUpdated.includes('addTags') &&
            propertiesUpdated.includes('removeTags');

        // if it is, no need to update status and assignee with currentUser
        let dataToUpdate = alertData;
        if (!isOnlyBulkAssign) {
            const currentUser = queryClient.getQueryData<UserOutput>(['currentUser']);
            const opts =
                forceToStart && !alertData.status ? { status: 'InProgress', assignee: currentUser?.login } : {};
            dataToUpdate = { ...dataToUpdate, ...opts };
        }

        return controller
            .bulkEdit<Partial<AlertInput>, void>(dataToUpdate, alertIds)
            .then((data) => {
                return data;
            })
            .catch(onError);
    },
    onImport: (alertId, caseId) =>
        controller
            .import(alertId, caseId)
            .then((data) => data)
            .catch(onError),
    onMultipleFollow: (alertIds) =>
        controller
            .multipleFollow(alertIds)
            .then(() => onSuccess(false, locale('notification.alert.follow')))
            .catch(onError),
    onMultipleUnfollow: (alertIds) =>
        controller
            .multipleUnfollow(alertIds)
            .then(() => onSuccess(false, locale('notification.alert.unfollow')))
            .catch(onError),
    onFollow: (alertId: string): void => {
        controller
            .follow(alertId)
            .then(() => onSuccess(false, locale('notification.alert.follow')))
            .catch(onError);
    },
    onUnfollow: (alertId: string): void => {
        controller
            .unFollow(alertId)
            .then(() => onSuccess(false, locale('notification.alert.unfollow')))
            .catch(onError);
    },
    onDelete: (alertId: string): void => {
        controller
            .delete(alertId)
            .then(() => {
                onSuccess(false, locale('notification.alert.delete'));
                history.push(RouteBuilder.compute('alerts.default'));
            })
            .catch(onError);
    },
    onBulkDelete: (alertIds: string[]): Promise<void> =>
        controller
            .bulkDelete(alertIds)
            .then(() => {
                onSuccess(false, locale('notification.alert.delete'));
            })
            .catch(onError),
    onMergeSeveralIntoCase: (alertIds, caseId): Promise<void | CaseOutput> =>
        controller
            .mergeSeveralIntoCase({ alertIds, caseId })
            .then((data) => {
                onSuccess?.(false, locale('notification.alert.create-case-several'));
                return data;
            })
            .catch(onError),
    customField: {
        onSet: (caseId: string, dataCustomField): Promise<AlertOutput> =>
            controller.setCustomField(dataCustomField as CustomFieldParams, caseId),
    },
    attachment: {
        onAdd: (alertId, attachments, refetch = true) =>
            controller
                .createAttachments(attachments, alertId)
                .then((attachments) => {
                    if (refetch) {
                        onSuccess?.(true, locale('attachment.action.success.add'));
                    }
                    return attachments;
                })
                .catch((error) => {
                    onError?.(error);
                    return { attachments: [] };
                }),
        onDelete: (alertId, attachmentId): Promise<void> => controller.deleteAttachment(attachmentId, alertId),
        onDownload: (alertId, attachmentId): Promise<Blob> => controller.downloadAttachment(attachmentId, alertId),
    },
    onUnlinkFromCase: (alertId, caseId) =>
        controller
            .unlinkFromCase(alertId, caseId)
            .then(() => onSuccess(true, 'Alert successfully unlinked from case!'))
            .catch(onError),
});
